import axios from "axios";
import store from "../store";
import { logout } from "../api/auth/authSlice";
import { showSnackbar } from "../api/snackbarSlice"; // Assume you have a snackbar slice to manage global snackbar
import appConfig from "../config/appConfig";

const apiClient = axios.create({
  baseURL: `${appConfig.apiBaseUrl}/api`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "X-API-Version": "20260101",
  },
});

const authApiClient = axios.create({
  baseURL: `${appConfig.apiBaseUrl}/auth`,
  withCredentials: true,
});

const billingClient = axios.create({
  baseURL: `${appConfig.apiBaseUrl}/billing`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "X-API-Version": "20260101",
  },
});

export async function endSession() {
  try {
    await authApiClient.post("/logout", {}, { skipSessionRedirect: true });
  } catch {
    // Clearing the cookie is best-effort when the network call fails.
  }

  store.dispatch(logout());
}

let sessionExpiryStarted = false;

function expireAuthenticatedSession() {
  if (!store.getState().auth?.isAuthenticated || sessionExpiryStarted) {
    return;
  }

  sessionExpiryStarted = true;
  store.dispatch(
    showSnackbar({
      message: "Session expired. Please log in again.",
      severity: "error",
    }),
  );
  endSession().finally(() => {
    window.location.href = "/login";
  });
}
const auditClient = axios.create({
  baseURL: `${appConfig.apiBaseUrl}/audit`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.response.use(
  (response) => {
    if (response.status === 202) {
      store.dispatch(
        showSnackbar({
          message: response.data?.message || "Request accepted successfully",
          severity: "success",
        }),
      );
    }
    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          if (error.config?.skipSessionRedirect) {
            break;
          }
          expireAuthenticatedSession();
          break;
        case 400:
          break;
        case 403:
          break;
        default:
          store.dispatch(
            showSnackbar({
              message:
                error.response.data.message || "An unexpected error occurred",
              severity: "error",
            }),
          );
      }
    }
    return Promise.reject(error);
  },
);

authApiClient.interceptors.response.use(
  (response) => {
    if (response.status === 202) {
      store.dispatch(
        showSnackbar({
          message: response.data?.message || "Request accepted successfully",
          severity: "success",
        }),
      );
    }
    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          if (error.config?.skipSessionRedirect) {
            break;
          }
          expireAuthenticatedSession();
          break;
        case 400:
          break;
        case 403:
          break;
        default:
          store.dispatch(
            showSnackbar({
              message:
                error.response.data.message || "An unexpected error occurred",
              severity: "error",
            }),
          );
      }
    }
    return Promise.reject(error);
  },
);

billingClient.interceptors.response.use(
  (response) => {
    if (response.status === 202) {
      store.dispatch(
        showSnackbar({
          message: response.data?.message || "Request accepted successfully",
          severity: "success",
        }),
      );
    }
    return response;
  },
  (error) => {
    if (error.response) {
      const { status } = error.response;

      switch (status) {
        case 401:
          if (error.config?.skipSessionRedirect) {
            break;
          }
          expireAuthenticatedSession();
          break;
        case 400:
          break;
        case 403:
          break;
        default:
          store.dispatch(
            showSnackbar({
              message:
                error.response.data.message || "An unexpected error occurred",
              severity: "error",
            }),
          );
      }
    }
    return Promise.reject(error);
  },
);

auditClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      expireAuthenticatedSession();
    }

    return Promise.reject(error);
  },
);

export { apiClient, authApiClient, billingClient, auditClient };
